// /settings/referrals — North Star §7 share link + attribution list (T5).
//
// Two surfaces:
//   1. Share link — copy-button-ready `https://wwaave.com/?r=<artistSlug>`.
//      The slug comes from the signed-in user's artist profile. Visitors who
//      land on the homepage with that param get a 60-day cookie that the
//      sign-up form then sends as `ref_artist_slug` (see worker auth.ts).
//      Hidden when the user has no artist profile — today the only
//      attribution direction is artist→host.
//
//   2. Attributions — list of venue accounts this artist has referred. Hits
//      GET /api/me/referrals; renders subscription state (earning starts with
//      the venue's first paid invoice — decided 2026-10-05) + first exchange.
//
// Future: when artist→artist attribution lands on artist_profiles, render a
// second list and merge — the server already shapes the rows with
// `kind: 'venue' | 'artist'`.

import {
  Anchor,
  Badge,
  Box,
  Button,
  Card,
  CopyButton,
  Group,
  Loader,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

import { AppShell } from '../../components/app-shell/AppShell';
import { SettingsTabs } from '../../components/app-shell/SettingsTabs';
import { EmptyState, MotionSection, PageHero, SectionTitle } from '../../components/ui';
import { ApiError, fetchOk } from '../../lib/api';
import { useAuth } from '../../lib/auth-context';

type ArtistMe = {
  ok: boolean;
  profile: {
    id: string;
    slug: string;
  };
};

type ReferralItem = {
  kind: 'venue' | 'artist';
  display_name: string;
  joined_at: string;
  subscription_state: string | null;
  first_exchange_completed_at: string | null;
};

type ReferralsResponse = {
  ok: true;
  items: ReferralItem[];
};

export const Route = createFileRoute('/settings/referrals')({
  component: ReferralsSettingsPage,
});

function ReferralsSettingsPage() {
  const { user, capabilities, isLoading } = useAuth();

  // Resolve the artist slug — required to build the share URL. Skip the fetch
  // entirely for non-artist users (the share UI is hidden in that branch).
  const artistQuery = useQuery({
    enabled: capabilities?.isArtist === true,
    queryKey: ['profiles', 'me', 'artist'],
    queryFn: async (): Promise<ArtistMe> => {
      const res = await fetchOk('/api/profiles/me/artist', { credentials: 'include' });
      return (await res.json()) as ArtistMe;
    },
  });

  const referralsQuery = useQuery({
    enabled: capabilities?.isArtist === true,
    queryKey: ['me', 'referrals'],
    queryFn: async (): Promise<ReferralsResponse> => {
      const res = await fetchOk('/api/me/referrals', { credentials: 'include' });
      return (await res.json()) as ReferralsResponse;
    },
    retry: (count, err) => {
      if (err instanceof ApiError && err.status >= 400 && err.status < 500) return false;
      return count < 2;
    },
  });

  if (isLoading) {
    return (
      <AppShell tone="form">
        <Loader />
      </AppShell>
    );
  }

  if (!user) {
    return (
      <AppShell tone="form">
        <Text>Sign in to manage your referrals.</Text>
      </AppShell>
    );
  }

  const slug = artistQuery.data?.profile.slug ?? null;
  // Use the canonical marketing host so the link reads cleanly when shared.
  // The homepage route accepts `?r=<slug>` on any origin we serve, so the
  // canonical URL still works on staging — but we'd rather hand out the
  // production host.
  const shareUrl = slug !== null ? `https://wwaave.com/?r=${slug}` : null;
  // Display without the https:// prefix for a cleaner read (Build Brief §6); the
  // full URL is still what gets copied.
  const shareDisplay = shareUrl !== null ? shareUrl.replace(/^https?:\/\//, '') : null;

  return (
    <AppShell tone="form">
      <Stack gap="lg">
        <MotionSection>
          <PageHero
            eyebrow="Settings"
            title="Referrals"
            lede="Share your link. When a host signs up through it, you earn 10% of every subscription payment they make (excluding GST), from their first payment."
          />
        </MotionSection>

        <SettingsTabs current="referrals" />

        {capabilities?.isArtist !== true ? (
          <Card withBorder padding="lg" radius="md">
            <Stack gap="sm">
              <SectionTitle title="Add an artist profile to share your link" order={3} />
              <Text size="sm" c="dimmed">
                The referral link uses your artist slug. Add an artist account
                from your profile settings, then come back here to share.
              </Text>
              {/* Future: when host→host or host→artist attribution is wired,
                  remove this gate and render a host-flavoured share link here. */}
            </Stack>
          </Card>
        ) : (
          <Card withBorder padding="lg" radius="md">
            <Stack gap="md">
              <SectionTitle title="Your share link" order={3} />
              <Text size="sm" c="dimmed">
                Paste this in DMs, on your gig posts, in your email signature,
                anywhere a venue might be. Visitors who land via this link get a
                60-day attribution window; we credit the host's first completed
                exchange to you.
              </Text>
              {artistQuery.isLoading || shareUrl === null ? (
                <Loader size="sm" />
              ) : (
                <Group gap="xs" wrap="nowrap">
                  <TextInput
                    value={shareDisplay ?? shareUrl}
                    readOnly
                    style={{ flex: 1 }}
                    aria-label="Your referral share URL"
                  />
                  <CopyButton value={shareUrl}>
                    {({ copied, copy }) => (
                      <Button
                        variant={copied ? 'filled' : 'light'}
                        onClick={copy}
                        color={copied ? 'teal' : 'brand'}
                      >
                        {copied ? 'Copied' : 'Copy'}
                      </Button>
                    )}
                  </CopyButton>
                </Group>
              )}
              <Text size="xs" c="dimmed">
                The link points at the homepage. Sharing a specific{' '}
                <Anchor href={`/artists/${slug ?? ''}`}>artist page</Anchor> also
                works. The same attribution kicks in when a visitor signs up.
              </Text>
            </Stack>
          </Card>
        )}

        {capabilities?.isArtist === true && (
          <Card withBorder padding={0} radius="md">
            <Box p="lg" pb="md">
              <SectionTitle title="Hosts who joined through you" order={3} />
            </Box>
            {referralsQuery.isLoading ? (
              <Box p="lg">
                <Loader size="sm" />
              </Box>
            ) : (referralsQuery.data?.items.length ?? 0) === 0 ? (
              <Box p="lg" pt={0}>
                <EmptyState
                  title="No attributions yet"
                  body="Once a host signs up through your link, they'll appear here with their subscription state."
                />
              </Box>
            ) : (
              <Stack gap={0}>
                {(referralsQuery.data?.items ?? []).map((item, idx) => (
                  <Box
                    key={`${item.kind}-${item.display_name}-${item.joined_at}`}
                    p="lg"
                    style={{
                      borderTop:
                        idx === 0 ? 'none' : '1px solid var(--mantine-color-ink-1)',
                    }}
                  >
                    <Group justify="space-between" align="flex-start" wrap="nowrap">
                      <Stack gap={4}>
                        <Group gap="xs">
                          <Text fw={600}>{item.display_name}</Text>
                          <Badge size="xs" variant="light" color="gray">
                            {item.kind === 'venue' ? 'Host' : 'Artist'}
                          </Badge>
                        </Group>
                        <Text size="xs" c="dimmed">
                          Joined {formatDate(item.joined_at)}
                          {item.subscription_state !== null
                            ? ` · Subscription ${item.subscription_state}`
                            : ''}
                        </Text>
                      </Stack>
                      <Stack gap={4} align="flex-end">
                        {item.subscription_state === 'active' || item.subscription_state === 'past_due' ? (
                          <Badge color="brand" variant="filled" size="sm">
                            Earning 10%
                          </Badge>
                        ) : item.subscription_state === 'trial' ? (
                          <Badge color="gray" variant="light" size="sm">
                            Earning from their first payment
                          </Badge>
                        ) : (
                          <Badge color="gray" variant="light" size="sm">
                            Not subscribed
                          </Badge>
                        )}
                        {item.first_exchange_completed_at !== null && (
                          <Text size="xs" c="dimmed">
                            First exchange {formatDate(item.first_exchange_completed_at)}
                          </Text>
                        )}
                      </Stack>
                    </Group>
                  </Box>
                ))}
              </Stack>
            )}
          </Card>
        )}
      </Stack>
    </AppShell>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '–';
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
